/**
 * Frontend error reporter — POSTs crashes to `/api/v1/error-report`, which logs
 * them server-side and pushes an alert to the Lark ops channel.
 *
 * Captures:
 * - React ErrorBoundary crashes (whole app + per-section)
 * - Unhandled promise rejections
 * - Uncaught window errors
 * - 5xx API responses (client errors are expected and stay silent)
 *
 * Rules this file lives by:
 * - Never throw. A reporter that breaks the page is worse than no reporter.
 * - Never block. `keepalive` so a report survives the navigation that follows.
 * - Deduplicate. One user hitting a render loop must not fan out to 400 alerts.
 */

import { pageIsMachineTranslated, translateGuardHits } from "@/shared/infra/translate-crash-guard";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";
const REPORT_URL = `${API_BASE}/api/v1/error-report`;
const RELEASE = import.meta.env.VITE_APP_VERSION || "dev";

/** Same error inside this window → dropped before it leaves the browser. */
const DEDUPE_WINDOW_MS = 60_000;
const MAX_TRACKED = 200;

const seen = new Map<string, number>();

function isDuplicate(key: string): boolean {
  const now = Date.now();
  const last = seen.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return true;

  seen.set(key, now);
  if (seen.size > MAX_TRACKED) {
    for (const [k, ts] of seen) {
      if (now - ts > DEDUPE_WINDOW_MS) seen.delete(k);
    }
  }
  return false;
}

type ReportType =
  | "error_boundary"
  | "section_error_boundary"
  | "unhandled_rejection"
  | "uncaught_error"
  | "api_error";

interface Report {
  type: ReportType;
  message: string;
  stack?: string;
  component?: string;
}

function send(report: Report): void {
  try {
    const payload = {
      ...report,
      url: window.location.href,
      user_agent: navigator.userAgent,
      release: RELEASE,
      locale: document.documentElement.lang || "",
      // A browser-translated page rewrites the text nodes React owns; it is the
      // first thing to check on any "works for me" crash report.
      machine_translated: pageIsMachineTranslated() || translateGuardHits() > 0,
    };

    void fetch(REPORT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* monitoring must never surface an error of its own */
    });
  } catch {
    /* ditto — a failure to report is not worth a second failure */
  }
}

/** Report a crash caught by a React error boundary. */
export function reportErrorBoundary(
  error: Error,
  componentStack?: string,
  scope: "app" | "section" = "app",
): void {
  const type: ReportType = scope === "section" ? "section_error_boundary" : "error_boundary";
  if (isDuplicate(`${type}:${error.message}`)) return;

  send({
    type,
    message: error.message,
    stack: [error.stack ?? "", componentStack ? `\nComponent stack:${componentStack}` : ""].join(
      "",
    ),
    component:
      componentStack
        ?.split("\n")
        .find((l) => l.trim())
        ?.trim() ?? "",
  });
}

/** Report an unhandled promise rejection. */
export function reportUnhandledRejection(reason: unknown): void {
  const message =
    reason instanceof Error
      ? reason.message
      : typeof reason === "string"
        ? reason
        : "Unknown rejection";
  if (isDuplicate(`rejection:${message}`)) return;

  send({
    type: "unhandled_rejection",
    message,
    stack: reason instanceof Error ? (reason.stack ?? "") : "",
  });
}

/** Report a failed API call. Only 5xx — 4xx are part of normal operation. */
export function reportApiError(path: string, status: number, detail = ""): void {
  if (status < 500) return;
  if (isDuplicate(`api:${path}:${status}`)) return;

  send({
    type: "api_error",
    message: `${status} on ${path}${detail ? `: ${detail.slice(0, 200)}` : ""}`,
    component: path,
  });
}

/**
 * Install window-level listeners. Call once, as early as possible — errors
 * thrown before this runs are lost.
 */
export function initErrorReporting(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("unhandledrejection", (event) => {
    reportUnhandledRejection(event.reason);
  });

  window.addEventListener("error", (event) => {
    if (!event.error) return; // resource load failures fire here too — skip them
    if (isDuplicate(`uncaught:${event.error.message}`)) return;
    send({
      type: "uncaught_error",
      message: event.error.message,
      stack: event.error.stack ?? "",
      component: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : "",
    });
  });
}
